import React, { useState, ChangeEvent, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Assessment, AssessmentSurvey } from '../../interfaces/assessment.interface';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useForm, FieldValues } from 'react-hook-form';

import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import User, { AssessorCompany, Company } from '../../interfaces/users.interface';
import throttle from 'lodash/throttle';
import getAuthSession from '../../services/auth';
import {
  TextField,
  InputAdornment,
  Grid,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import axios, { AxiosResponse } from 'axios';
import { Survey } from '../../interfaces/survey.interface';
import SurveySelectList from '../Vendors/Forms/SurveySelectList';
import { assignAssessor, updateAssessor } from '../../services/workflowThunks';
import { fetchWorkflows } from '../../services/workflow';
import { Workflow } from '../../interfaces/workflow.interface';
import { fetchSurvey } from '../../services/surveys';
import { USER_TYPES } from '../../services/user';
import { getSiteCloudStatus } from '../../services/assessments';
import { AsyncSaveButton } from '../../components/buttons/AsyncSaveButton';

export default function AssessmentForm(props: { assessment?: Assessment, modalOpen: boolean, company: Company, closeCallback: any }) {
  const {
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      assessor: props.assessment?.assessor ? (props.assessment.assessor as Company).id : undefined,
      assignedAssessor: props.assessment?.assignedAssessor ? (props.assessment.assignedAssessor as User).id : undefined,
    } as {
      assessor?: number,
      assignedAssessor?: number,
    },
  });
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.user);
  const workflow = useAppSelector(state => state.workflow?.workflows?.find(wf => wf.assessment === props.assessment?.id));
  const [filteredSurveys, setFilteredSurveys] = useState<Survey[] | undefined>(undefined);
  const [selectedSurveys, setSelectedSurveys] = useState<Survey[]>([]);
  const [newSelectedSurveys, setNewSelectedSurveys] = useState<Survey[]>([]);
  const [, setNewSelectedAssessmentSurveys] = useState<AssessmentSurvey[]>([]);
  const [errorText, setErrorText] = useState('');
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [isInPerson, setIsInPerson] = useState<boolean>(true);
  const [isZeroTrustAssessment, setIsZeroTrustAssessment] = useState<boolean>(false);

  const surveysCallBack = (surs:Survey[], assSurs:AssessmentSurvey[]) => {
    setNewSelectedSurveys(surs);
    setNewSelectedAssessmentSurveys(assSurs);

    // If workflow is not provided, find one matching the selected survey by bpSurvey or baseline
    if (!workflow && surs.length > 0) {
      const selectedSurvey = surs[surs.length - 1];
      const matchingWorkflow = workflows.find(wf =>
        wf.bpSurvey === selectedSurvey.id || wf.baseline === selectedSurvey.id,
      );
      if (matchingWorkflow) {
        setWorkflows([matchingWorkflow]);
      }
    } else if (workflow) {
      setWorkflows([workflow]);
    }
  };

  const setIsInPersonCallback = (isInPersonBoolean: boolean) => {
    setIsInPerson(isInPersonBoolean);
  };

  const [modalOpen, setModalOpen] = useState(props.modalOpen);
  const [itemList, setItemList] = useState<AssessorCompany[]>([]);
  const [JWToken, setJWToken] = useState('');
  const userRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showInPersonModal, setShowInPersonModal] = useState<boolean>(false);

  const handleShowInPersonModal = useCallback(() => {
    setShowInPersonModal(true);
  }, [setShowInPersonModal]);

  useEffect(() => {
    if (!props.assessment || !modalOpen) return;
    (props.assessment.surveys as  AssessmentSurvey[]).forEach(sur => {
      fetchSurvey(typeof sur.survey === 'number' ? sur.survey : sur.survey.id).then(nsur => {
        setFilteredSurveys(fs => fs ? [...fs, nsur] : [nsur]);
        setSelectedSurveys(fs => fs ? [...fs, nsur] : [nsur]);
      });
    });
  }, [props.assessment, modalOpen]);

  useEffect(() => {
    if (!props.assessment || !JWToken || !modalOpen) return;
    reset({
      assessor: props.assessment?.assessor ? (props.assessment.assessor as Company).id : undefined,
      assignedAssessor: props.assessment?.assignedAssessor ? (props.assessment.assignedAssessor as User).id : undefined,
    });

    if (!filteredSurveys) return;

    let sSurveys:Survey[] = [];
    (props.assessment.surveys as AssessmentSurvey[]).forEach(sur => {
      const fSurvey = filteredSurveys.find(nsur => nsur.id === (sur.survey as Survey).id);
      if (fSurvey) sSurveys.push(fSurvey);
    });
  }, [props.assessment, JWToken, modalOpen]);

  const handleFetchWorkflows = async () => {
    const wfs = await fetchWorkflows({ status: 'submittedForAssessment', service_provider: props.company.id }, true);
    setWorkflows(wfs);
    if (!wfs.length) setFilteredSurveys([]);
    wfs.filter(wf => wf.bpSurvey).forEach(wf =>
      fetchSurvey(wf.bpSurvey!)
        .then(s => setFilteredSurveys(fs => fs ? fs.find(ffs => ffs.id === s.id) ? [...fs] : [...fs, s] : [s]))
        .catch(() => {
          console.info(`Unable to fetch matching survey for workflow ${wf.id}`);
        }),
    );
  };

  useEffect(() => {
    setModalOpen(props.modalOpen);
    if (props.modalOpen) handleFetchWorkflows();
    if (!props.modalOpen) {
      reset({
        assessor: undefined,
        assignedAssessor: undefined,
      });
      setFilteredSurveys(undefined);
      setWorkflows([]);
      setSelectedSurveys([]);
      setItemList([]);
    }

  }, [props.assessment, JWToken, modalOpen]);

  useEffect(() => {
    setModalOpen(props.modalOpen);
    if (props.modalOpen) handleFetchWorkflows();
    if (!props.modalOpen) {
      reset({
        assessor: undefined,
        assignedAssessor: undefined,
      });
      setFilteredSurveys(undefined);
      setWorkflows([]);
      setSelectedSurveys([]);
      setItemList([]);
    }
  }, [props.modalOpen]);

  const onSubmit = async (data: FieldValues) => {
    if (newSelectedSurveys.length === 0) { setErrorText('You must Select a Site or Application'); return; }
    if (!data.assessor) { setErrorText('You must Select an Assessment Company'); return; }
    if (!data.assignedAssessor) { setErrorText('You must Select an Assessor'); return; }
    setErrorText('');
    setIsSaving(true);

    // Find the workflow matching the selected survey by bpSurvey or baseline
    const selectedSurvey = newSelectedSurveys[newSelectedSurveys.length - 1];
    const matchingWorkflow = workflows.find(wf =>
      wf.bpSurvey === selectedSurvey.id || wf.baseline === selectedSurvey.id,
    ) ?? workflow;

    if (!matchingWorkflow) {
      setErrorText('No matching workflow found for the selected survey');
      return;
    }

    // Fetch environment flags from baseline
    let isOnPremise = false;
    let isCloud = false;
    if (matchingWorkflow.baseline) {
      const baselineId = typeof matchingWorkflow.baseline === 'object'
        ? (matchingWorkflow.baseline as { id: number }).id
        : matchingWorkflow.baseline;
      const siteCloudStatus = await getSiteCloudStatus(baselineId);
      isOnPremise = siteCloudStatus.site || siteCloudStatus.both;
      isCloud = siteCloudStatus.cloud || siteCloudStatus.both;
    }

    try {
      if (!props.assessment) {
        // Create initial assessment
        const result = await dispatch(assignAssessor({
          isOnPremise,
          isCloud,
          isInPerson,
          assessorID: data.assessor,
          workflowId: matchingWorkflow.id,
          assignedAssessorId: data.assignedAssessor,
        })).unwrap();
        reset({
          assessor: undefined,
          assignedAssessor: undefined,
        });
        setSelectedSurveys([]);
        setItemList([]);
        props.closeCallback(result);
      } else {
        // Update existing assessment
        const result = await dispatch(updateAssessor({
          isOnPremise,
          isCloud,
          isInPerson,
          assessorID: data.assessor,
          workflowId: matchingWorkflow.id,
          assignedAssessorId: data.assignedAssessor,
        })).unwrap();
        reset({
          assessor: undefined,
          assignedAssessor: undefined,
        });
        setSelectedSurveys([]);
        setItemList([]);
        props.closeCallback(result);
      }
    } catch (error) {
      console.error('Error in onSubmit:', error);
      setErrorText('Failed to assign assessor. Please try again.');
    }
    reset({
      assessor: undefined,
      assignedAssessor: undefined,
    });
    setSelectedSurveys([]);
    setItemList([]);
    props.closeCallback();
    setIsSaving(false);
  };

  useEffect(() => {
    getAuthSession().then((session) => {
      setJWToken(session.getIdToken().getJwtToken());
    });
  });

  const fetchItems =  (searchText: string) => {
    if (isZeroTrustAssessment) searchText += '&has_zero_trust_certification=true';
    const url = user?.email.includes('@motionpictures.org')
      ?  `${process.env.REACT_APP_BASE_API}/companies/assessor-companies/?limit=9999&search=${searchText}` // Fetch all assessors for MPA users
      : `${process.env.REACT_APP_BASE_API}/companies/assessor-companies/?limit=9999&has_assessor_level=true&search=${searchText}`;
    if (!searchText || !JWToken) return;
    try {
      axios.get(
        url,
        {
          headers: { 'Authorization': `Bearer ${JWToken}` },
        },
      ).then((response: AxiosResponse) => {
        setItemList(response.data.results as AssessorCompany[]);
      });
    } catch (error: any) {
      console.error(error.response.data);
    }
  };

  const handleAssessorChange = (event: any, item: any | null) => {
    if (!item) return;
    setValue('assessor', item.id);
    setValue('assignedAssessor', item.employees.length > 0 ? item.employees[0].id : undefined);
    if (userRef && userRef.current) {
      const autoCRef = (userRef.current as HTMLElement).getElementsByClassName('MuiAutocomplete-clearIndicator')[0] as HTMLElement;
      if (autoCRef) autoCRef.click();
    }
  };

  const throttledFetchItems = throttle(fetchItems, 2500, { leading: true, trailing: true });

  const handleInputChange = async (event: ChangeEvent<{ value: unknown }>) => {
    await throttledFetchItems(event.target.value as string);
  };

  return (<>
    {props.company && filteredSurveys &&
    <React.Fragment>
      <StandardDialog title={(props.assessment ? 'Edit' : 'Create New') + ' Assessment' } isOpen={modalOpen} handleClose={() => props.closeCallback()}>
      {( (props.company.isPublished && user?.type === USER_TYPES.VENDOR_ADMIN) || user?.type == USER_TYPES.TPN_ADMIN) && <Box component='form' sx={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} alignItems="center">
            {isZeroTrustAssessment && <Alert severity="info">Your TPN Assessment requires a Zero Trust accredited assessor.</Alert>}
            <Grid item xs={12}>
              <SurveySelectList surveys={filteredSurveys ? filteredSurveys : []} selectedSurveys={selectedSurveys} company={props.company} assessment={props.assessment} surveysCallBack={surveysCallBack} handleShowInPersonModal={handleShowInPersonModal} handleIsZeroTrustAssessment={setIsZeroTrustAssessment} isInPersonCallback={setIsInPersonCallback} />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                id='assessor'
                fullWidth
                options={itemList}
                getOptionLabel={(option) => option?.assessorLevel ? option.name + ' - ' + option.assessorLevel.descriptor : option.name}
                isOptionEqualToValue={(option, value) => option.id == value.id}
                onChange={handleAssessorChange}
                disableClearable={true}
                defaultValue={props.assessment ? (props.assessment.assessor as AssessorCompany) : undefined}
                renderInput={(params) =>
                  <TextField
                  {...params}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  onChange={handleInputChange}
                  disabled={user?.type === USER_TYPES.ASSESSOR}
                  placeholder='Search Assessors'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (<>
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                      {params.InputProps.endAdornment}
                    </>),
                  }}
                /> }
              />
            </Grid>
          </Grid>
          {errorText.length > 0 && <Typography color="error" variant="caption">{errorText}</Typography>}
          <StandardDialogActions>
            <Button variant="outlined" onClick={() => props.closeCallback()}>Cancel</Button>
            <AsyncSaveButton
              type="submit"
              variant="contained"
              label={'Save'}
              savingLabel="Saving..."
              isSaving={isSaving}
            />
          </StandardDialogActions>
        </Box>}
        {(!props.company.isPublished && user?.type != USER_TYPES.TPN_ADMIN) && <Box>
          <Typography>Your membership is not currently active. Please ensure you have paid the membership fee - and, if applicable, signed the TPN membership agreement.</Typography>
          <StandardDialogActions>
            <Button variant="outlined" onClick={() => props.closeCallback()}>Close</Button>
          </StandardDialogActions>
        </Box>}
      </StandardDialog>
      <StandardDialog title='Notice' isOpen={showInPersonModal} handleClose={() => setShowInPersonModal(false)} maxWidth="sm">
        <Typography>Studios often prefer an on-site assessment. Please check with your Studio customers if you are unsure.</Typography>
          <StandardDialogActions>
            <Button variant="outlined" onClick={() => setShowInPersonModal(false)}>Close</Button>
          </StandardDialogActions>
      </StandardDialog>
    </React.Fragment>
    }
  </>
  );
}
